import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import cnames from 'classnames';
import get from 'lodash/get';
import _styles from './styles.module.css';
import getElementType from '../utils/getElementType';
import childTypeEquals from '../utils/childTypeEquals';
import ChevronRightIcon from '../Icons/ChevronRightBig';
import Box from '../Box';

const ListMedia = (props?: any) => {
  const {
    children,
    className,
    styles,
  } = props;

  const classNames = cnames(styles.listMedia, {
    [className]: className,
  });

  return (
    <div className={classNames}>
      { children }
    </div>
  );
};

(ListMedia as any).propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  styles: PropTypes.shape({}),
};

(ListMedia as any).defaultProps = {
  className: '',
  styles: _styles,
};

(ListMedia as any).displayName = 'ListMedia';

const ListContent = (props?: any) => {
  const {
    children,
    className,
    styles,
  } = props;

  const classNames = cnames(styles.listContent, {
    [className]: className,
  });

  return (
    <div className={classNames}>
      { children }
    </div>
  );
};

(ListContent as any).propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  styles: PropTypes.shape({}),
};

(ListContent as any).defaultProps = {
  className: '',
  styles: _styles,
};

(ListContent as any).displayName = 'ListContent';

const ListItem = (props?: any) => {
  const {
    children,
    className,
    arrow,
    element,
    styles,
    bulletMedia,
    ...rest
  } = props;

  const Element = getElementType(ListItem, props);

  const containsMediaOrContent = React.Children
    .toArray(children)
    .some((child?: any) => childTypeEquals(child, [(ListMedia as any).displayName, (ListContent as any).displayName]));

  const classNames = cnames(styles.listItem, {
    [className]: className,
    [styles.flex]: containsMediaOrContent,
    [styles.arrow]: arrow,
    [styles.customBulletedListItem]: bulletMedia,
  });

  return (
    <Element className={classNames} {...rest}>
      { bulletMedia && <div className={styles.bulletMedia}>{bulletMedia}</div> }
      { children }
      { arrow && <Box className={styles.arrowIcon}><ChevronRightIcon /></Box>}
    </Element>
  );
};

(ListItem as any).propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  /**
    * Define what element to use for the list
  */
  element: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func,
  ]),
  /**
    * Display an arrow at the end of the list item
    */
  arrow: PropTypes.bool,
  /**
    * Display a custom bullet
    */
  bulletMedia: PropTypes.node,
  /**
    * Custom styles on the list item
    */
  styles: PropTypes.shape({}),
};

(ListItem as any).defaultProps = {
  className: '',
  element: 'li',
  arrow: false,
  bulletMedia: null,
  styles: _styles,
};

(ListItem as any).displayName = 'ListItem';

const List = forwardRef((props?: any, ref?: any) => {
  const {
    children,
    className,
    ordered,
    bulleted,
    bulletMedia,
    divided,
    relaxed,
    padded,
    styles,
    borderTop,
    borderBottom,
    ...rest
  } = props;

  const Element = ordered ? 'ol' : getElementType(List, props);

  const classNames = cnames(styles.list, {
    [className]: className,
    [styles.bulleted]: bulleted || bulletMedia,
    [styles.customBulleted]: bulletMedia,
    [styles.divided]: divided,
    [styles.relaxed]: relaxed || divided,
    [styles.padded]: padded,
    [styles.borderTop]: borderTop,
    [styles.borderBottom]: borderBottom,
  });

  return (
    <Element
      className={classNames}
      {...rest}
      ref={ref}
    >
      {
      React.Children
        .toArray(children)
        .filter(Boolean)
        .map((child?: any) => (
          React.cloneElement(child, {
            bulletMedia: get(child, 'props.bulletMedia') || bulletMedia,
          })
        ))
      }
    </Element>
  );
}) as any;

(List as any).propTypes = {
  /**
    * The content of the list, typically ListItems
    */
  children: PropTypes.node.isRequired,
  /**
    * Custom className. Recommended for positioning in a layout only
    */
  className: PropTypes.string,
  /**
    * The HTML element to use for the List
    */
  element: PropTypes.string,
  /**
    * When true, displays numbered bullets if bulleted is also true
    */
  ordered: PropTypes.bool,
  /**
    * When true, displays bullets for each ListItem
    */
  bulleted: PropTypes.bool,
  /**
    * When true, displays lines between ListItems
    */
  divided: PropTypes.bool,
  /**
    * When true, leaves more space between each ListItem
    */
  relaxed: PropTypes.bool,
  /**
    * When true, adds padding on the left and right of the ListItems
    */
  padded: PropTypes.bool,
  /**
    * The CSS classNames for this component. NEVER manually specify this prop
    */
  styles: PropTypes.shape({}),
  /**
    * Display a separating border above the first list item
    */
  borderTop: PropTypes.bool,
  /**
    * Display a separating border below the last list item
    */
  borderBottom: PropTypes.bool,
  /**
   * Display a custom bullet point
   */
  bulletMedia: PropTypes.node,
};

(List as any).defaultProps = {
  className: '',
  element: 'ul',
  ordered: false,
  bulleted: false,
  divided: false,
  relaxed: false,
  padded: false,
  styles: _styles,
  borderTop: false,
  borderBottom: false,
};

(List as any).displayName = 'List';

export { ListItem, ListContent, ListMedia };
export default List;
